import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Fab,
  Tooltip,
  IconButton,
  Dialog,
  DialogContent,
  Menu,
  MenuItem,
  DialogTitle,
  DialogActions,
  DialogContentText,
  Checkbox,
  Box,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import CompareIcon from '@mui/icons-material/Compare';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Loading from '../../../../components/loading';
import SuccessMessage from '../../../../components/successMessage';
import ErrorMessage from '../../../../components/errorMessage';
import ImageModal from '../imageModal';
import { useImages } from '../hooks/useImages';
import { useImageActions } from '../hooks/useImageActions';
import FileTypeFilter from '../fileTypeFilter';
import { filterByFileType, isPdf } from '../fileType';

export default function ImageListMobile() {
  const { patientId } = useParams();

  const [isLoading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [isNewImageModalOpen, setIsNewImageModalOpen] = useState(false);
  const [selectedImageToView, setSelectedImageToView] = useState(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showModalToDelete, setShowModalToDelete] = useState(false);

  const [selectedToCompare, setSelectedToCompare] = useState([]);
  const [imagesToCompare, setImagesToCompare] = useState(null);

  const [fileTypeFilter, setFileTypeFilter] = useState('all');

  const {
    images,
    isLoading: isImagesLoading,
    error: listError,
    refetch: getImagesFromPatient,
  } = useImages(patientId);
  const { remove, fetchUrl } = useImageActions();
  const visibleFiles = filterByFileType(images, fileTypeFilter);

  useEffect(() => {
    if (listError) {
      setShowErrorMessage(true);
      setErrorMessage(listError);
    }
  }, [listError]);

  const onImageSaved = (returnFromApi) => {
    setIsNewImageModalOpen(false);
    if (returnFromApi.data) {
      setShowSuccessMessage(true);
      setSuccessMessage(returnFromApi.data);
    } else {
      setShowErrorMessage(true);
      setErrorMessage(returnFromApi.response?.data);
    }
    getImagesFromPatient();
  };

  const deleteImage = (imageId) => {
    setLoading(true);
    remove(imageId)
      .then((returnFromApi) => {
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
        setLoading(false);
        getImagesFromPatient();
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
      });
  };

  const menuClose = () => {
    setAnchorEl(null);
  };

  const confirmDelete = () => {
    setShowModalToDelete(false);
    if (selectedImage) {
      deleteImage(selectedImage.id);
    }
  };

  function openImage(image) {
    setLoading(true);
    fetchUrl(image.id)
      .then((returnFromApi) => {
        setLoading(false);
        setSelectedImageToView({ url: returnFromApi.data, isPdf: isPdf(image) });
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
      });
  }

  function openModalToDelete() {
    setShowModalToDelete(true);
  }

  const isSelectedToCompare = (image) => selectedToCompare.some((img) => img.id === image.id);

  const toggleCompareSelection = (image) => {
    setSelectedToCompare((previousSelection) => {
      if (previousSelection.some((img) => img.id === image.id)) {
        return previousSelection.filter((img) => img.id !== image.id);
      }
      if (previousSelection.length >= 2) {
        return previousSelection;
      }
      return [...previousSelection, image];
    });
  };

  const openCompare = () => {
    if (selectedToCompare.length !== 2) return;
    setLoading(true);
    Promise.all(selectedToCompare.map((image) => fetchUrl(image.id)))
      .then((responses) => {
        setImagesToCompare(
          selectedToCompare.map((image, index) => ({
            ...image,
            url: responses[index].data,
          })),
        );
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        console.error('ops! ocorreu um erro' + returnFromApi);
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <Loading isToHidden={!isLoading && !isImagesLoading} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Card sx={{ borderRadius: '16px', boxShadow: 'none' }}>
        <CardHeader
          title="Imagens e Documentos"
          titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
          action={
            <Tooltip title="Marque duas imagens para visualizá-las uma em cima da outra.">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CompareIcon />}
                  onClick={openCompare}
                  disabled={selectedToCompare.length !== 2}
                >
                  Comparar
                  {selectedToCompare.length > 0 ? ` (${selectedToCompare.length}/2)` : ''}
                </Button>
              </span>
            </Tooltip>
          }
        />
        <CardContent>
          <FileTypeFilter value={fileTypeFilter} onChange={setFileTypeFilter} />
          {!isLoading && visibleFiles.length === 0 ? (
            <Typography align="center" sx={{ py: 4 }}>
              Nenhum arquivo cadastrado ainda.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {visibleFiles.map((image) => (
                <Box
                  key={image.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1,
                    borderRadius: 2,
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Tooltip
                    title={
                      isPdf(image)
                        ? 'Documentos PDF não podem ser comparados.'
                        : !isSelectedToCompare(image) && selectedToCompare.length >= 2
                          ? 'Você só pode comparar duas imagens por vez.'
                          : 'Selecionar para comparar'
                    }
                  >
                    <span>
                      <Checkbox
                        size="small"
                        sx={{ p: 0.5 }}
                        checked={isSelectedToCompare(image)}
                        disabled={
                          isPdf(image) ||
                          (!isSelectedToCompare(image) && selectedToCompare.length >= 2)
                        }
                        onChange={() => toggleCompareSelection(image)}
                      />
                    </span>
                  </Tooltip>

                  {isPdf(image) ? (
                    <Box
                      onClick={() => openImage(image)}
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: 1,
                        cursor: 'pointer',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <PictureAsPdfIcon color="error" sx={{ fontSize: 36 }} />
                    </Box>
                  ) : image?.thumbnailUrl ? (
                    <Box
                      component="img"
                      src={image.thumbnailUrl}
                      alt={image.description || 'Miniatura'}
                      loading="lazy"
                      onClick={() => openImage(image)}
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: 1,
                        objectFit: 'cover',
                        cursor: 'pointer',
                        flexShrink: 0,
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                      }}
                    />
                  ) : null}

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {image.date}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {image.description}
                    </Typography>
                  </Box>

                  <IconButton
                    size="small"
                    onClick={(event) => {
                      setAnchorEl(event.currentTarget);
                      setSelectedImage(image);
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      <Fab
        color="primary"
        aria-label="adicionar arquivo"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
        }}
        onClick={() => setIsNewImageModalOpen(true)}
      >
        <AddIcon />
      </Fab>

      <ImageModal
        open={isNewImageModalOpen}
        onCloseImageModal={() => setIsNewImageModalOpen(false)}
        onImageSaved={onImageSaved}
        patientId={patientId}
      />

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={menuClose}>
        <MenuItem
          onClick={() => {
            openImage(selectedImage);
            menuClose();
          }}
        >
          Visualizar
        </MenuItem>
        <MenuItem
          onClick={() => {
            openModalToDelete();
            menuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          Excluir
        </MenuItem>
      </Menu>

      <Dialog open={Boolean(selectedImageToView)} maxWidth="xl">
        <Tooltip title="Fechar">
          <IconButton
            aria-label="close"
            onClick={() => setSelectedImageToView(null)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              backgroundColor: 'white',
              color: 'black',
              borderRadius: '50%',
              '&:hover': { backgroundColor: 'lightgray' },
            }}
          >
            <CloseIcon fontSize="large" />
          </IconButton>
        </Tooltip>
        {selectedImageToView?.isPdf ? (
          <Box
            sx={{
              p: 4,
              pt: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <PictureAsPdfIcon color="error" sx={{ fontSize: 64 }} />
            <Button
              variant="contained"
              href={selectedImageToView.url}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<OpenInNewIcon />}
            >
              Abrir PDF
            </Button>
          </Box>
        ) : (
          selectedImageToView && (
            <img
              src={selectedImageToView.url}
              alt="Imagem"
              style={{ maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain' }}
            />
          )
        )}
      </Dialog>

      <Dialog open={Boolean(imagesToCompare)} fullScreen>
        <Loading isToHidden={!isLoading && !isImagesLoading} />
        <Tooltip title="Fechar">
          <IconButton
            aria-label="close"
            onClick={() => setImagesToCompare(null)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              zIndex: 2,
              backgroundColor: 'white',
              color: 'black',
              borderRadius: '50%',
              '&:hover': { backgroundColor: 'lightgray' },
            }}
          >
            <CloseIcon fontSize="large" />
          </IconButton>
        </Tooltip>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {(imagesToCompare || []).map((image) => (
              <Box
                key={image.id}
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              >
                <Box
                  component="img"
                  src={image.url}
                  alt={image.description || 'Imagem'}
                  sx={{ maxWidth: '100%', maxHeight: '40vh', objectFit: 'contain' }}
                />
                <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 'bold' }}>
                  {image.date}
                </Typography>
                {image.description ? (
                  <Typography variant="body2" color="text.secondary" align="center">
                    {image.description}
                  </Typography>
                ) : null}
              </Box>
            ))}
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={showModalToDelete} onClose={() => setShowModalToDelete(false)}>
        <DialogTitle>{'Confirmar Exclusão'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir este arquivo? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowModalToDelete(false)}>Cancelar</Button>
          <Button onClick={confirmDelete} color="error">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
